import { DoubleRightOutlined } from '@ant-design/icons';
import { Modal, Descriptions, Space, Button, Collapse, Switch } from 'antd';
import classnames from 'classnames';
import { isEqual, unset, get, isEmpty } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useState } from 'react';
import IconClose from '@/assets/images/icons/icon-close.react.svg?react';
import { ConfigTabType, isConfigManage } from '@/presentation/pages/setting/app-deliver-config/api';
import { RecordType } from '@/presentation/pages/setting/app-deliver-config/components/configuration-content';
import styles from '@/presentation/pages/setting/app-deliver-config/components/index.module.scss';
import { DiffCustomizeData } from '@/presentation/pages/setting/app-deliver-config/config-modal/edit/diff-customize-data';
import { DiffRenderSubConfigItem } from '@/presentation/pages/setting/app-deliver-config/config-modal/edit/diff-sub-config-item';
import { IConfig, operatorOptions } from '@/presentation/pages/setting/app-deliver-config/config-modal/edit/index';
import { getAppVersionModeLabel } from '@/presentation/pages/setting/app-deliver-config/config-modal/view';
import { CUSTOMIZE_CONFIG } from '@/presentation/pages/setting/app-deliver-config/constant';
import { SampleRatio } from '@/presentation/pages/setting/app-deliver-config/type';
import {
  isCustomize,
  transform2Config,
  transform2Form,
  transform2Value,
} from '@/presentation/pages/setting/app-deliver-config/utils';
import { useStores } from '@/presentation/store';
import style from './index.module.scss';

interface IProps {
  prevForm: IConfig;
  nextForm: IConfig;
  prevCode: string;
  nextCode: string;
  visible: boolean;
  onSubmit: () => void;
  onHide: () => void;
  type: 'view' | 'update';
  tab: ConfigTabType;
}


export enum DiffType {
  NEXT=0,
  PREV=1
}

const CollapsePanel = Collapse.Panel;

const labelStyle: React.CSSProperties = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '22px',
  width: '70px',
  justifyContent: 'flex-end',
  color: '#7B889C',
  flex: 'none',
};

export const DiffModal: FC<IProps> = observer((props) => {
  const [visible, setVisible] = useState(props.visible);
  const [activeKey, setActiveKey] = useState<string[]>([]);
  const { prevCode, nextCode, onSubmit, onHide, prevForm, nextForm, type, tab } = props;
  const { configs = {}, androidFullConfig = {}, iosFullConfig = {}, flutterFullConfig = {},
    windowsFullConfig = {}, harmonyOSFullConfig = {} } = useStores().ConfigStore;
  const { configMode = {} } = configs;
  const fullConfigMode = {
    ...configMode.android,
    ...configMode.ios,
    ...configMode.flutter,
  };
  const platformModule = {
    ...fullConfigMode,
    [CUSTOMIZE_CONFIG.name]: CUSTOMIZE_CONFIG.label,
  };
  const {
    chunkConfig: prevChunkConfig,
    sampleRatio: prevSampleRatio,
  } = transform2Form(
    prevCode, androidFullConfig, iosFullConfig, flutterFullConfig,
    windowsFullConfig, harmonyOSFullConfig, fullConfigMode,
  ) || {};
  const {
    chunkConfig: nextChunkConfig,
    sampleRatio: nextSampleRatio,
  } = transform2Form(
    transform2Config(nextCode), androidFullConfig, iosFullConfig, flutterFullConfig,
    windowsFullConfig, harmonyOSFullConfig, fullConfigMode,
  ) || {};
  const nextV7 = get(transform2Value(nextCode), 'v7') || {};

  const prevExcessNames =  prevChunkConfig.filter((item) => {
    const key = Object.keys(item)?.[0];
    const nextNames = nextChunkConfig.map(item => Object.keys(item)?.[0]);
    return !nextNames.includes(key);
  }).map(item => Object.keys(item)?.[0]);

  const nextExcessNames =  nextChunkConfig.filter((item) => {
    const key = Object.keys(item)?.[0];
    const prevNames = prevChunkConfig.map(item => Object.keys(item)?.[0]);
    return !prevNames.includes(key);
  }).map(item => Object.keys(item)?.[0]);

  if (!isConfigManage(tab)) {
    unset(prevForm, 'productId');
    unset(nextForm, 'productId');
  }
  const isDiffCode = () => !isEqual(prevChunkConfig, nextChunkConfig) || !isEqual(prevSampleRatio, nextSampleRatio);
  const isDiffForm = () => !isEqual(prevForm, nextForm);

  useEffect(() => {
    if (props.visible) {
      setVisible(true);
      return;
    }
    setVisible(false);
  }, [prevCode, nextCode, props.visible]);


  const diffForm = getDiffForm(prevForm, nextForm, tab);
  const close = () => {
    setVisible(false);
    setActiveKey([]);
    onHide();
  };
  const prevConfig = (config: { label: string, info: string, isSame: boolean }[]) => (<div className={style.prevConfig}>
    {config.map(item => <div key={item.label} className={style.configItem}>
        <span className={classnames({ [style.configLabel]: true,
          [style.configPrev]: !item.isSame })}>{item.label}</span>
      <span className={classnames({ [style.configValue]: true,
        [style.configPrev]: !item.isSame })}>
          {item.info}
        </span>
    </div>)}
    </div>);

  const nextConfig = (config: { label: string, info: string, isSame?: boolean }[]) => (
    <div className={style.nextConfig}>
      {config.map(item => <div key={item.label} className={style.configItem}>
        <span className={classnames({ [style.configLabel]: true,
          [style.configSame]: !item.isSame })}>{item.label}</span>
        <span className={classnames({ [style.configValue]: true,
          [style.configSame]: !item.isSame })}>
          {item.info}
        </span>
      </div>)}
  </div>);

  // 左边展开
  const prevCollapseChange = (keys: string | string[]) => {
    if (!Array.isArray(keys)) {
      return;
    }

    setActiveKey(prevKeys => ([...prevKeys.filter(key => nextExcessNames.includes(key)), ...keys]));
  };

  // 右边展开
  const nextCollapseChange = (keys: string | string[]) => {
    if (!Array.isArray(keys)) {
      return;
    }

    setActiveKey(prevKeys => ([...prevKeys.filter(key => prevExcessNames.includes(key)), ...keys]));
  };


  const getConfigInfo = (name: string, platformConfig: RecordType, configContent: any[]) => {
    const config = get(platformConfig, name)
      || get(platformConfig, 'features')?.find((item: RecordType) => item.name === name)
      || configContent.find(item => item?.[name])?.[name]
      || {};
    const options = Object.keys(config)?.map(item => ({ label: item, value: item }));

    return {
      options,
      config,
    };
  };
  const renderSampleRatio = (checked: boolean) => <Space align={'center'}>
    <span style={{ marginLeft: 29 }}>关闭所有监控项</span>
    <Switch checked={!checked} disabled={true}/>
  </Space>;


  const prevConfigContent = (configContent: any[]) => <Collapse
    className={style.prevConfigContent}
    activeKey={activeKey}
    onChange={prevCollapseChange}
  >
    {prevSampleRatio === SampleRatio.ON ? <CollapsePanel
      header={renderSampleRatio(!!prevSampleRatio)}
      key={'sampleRatio'}
      showArrow={false}
      className={classnames({
        [styles.globalSwitch]: true,
        [style.removedCollapsePanel]: !isEqual(prevSampleRatio, nextSampleRatio) })}
      collapsible={'disabled'}
    /> : null}
      {configContent.map((configItem) => {
        const name = Object.keys(configItem)?.[0];
        const value = configItem[name];
        const nextConfigItem = nextChunkConfig.find(item => item[name]) || {};
        const nextValue = nextConfigItem?.[name] || {};
        const isCustom = isCustomize(
          { name }, androidFullConfig, iosFullConfig, flutterFullConfig,
          windowsFullConfig, harmonyOSFullConfig, fullConfigMode,
        )
          || isConfigManage(props.tab);

        // 标记删除的
        return <CollapsePanel
          className={classnames({
            [style.removedCollapsePanel]: !isEqual(value, nextValue) })}
          header={(name || '').indexOf(CUSTOMIZE_CONFIG.name) === 0 ? get(platformModule, CUSTOMIZE_CONFIG.name) : get(platformModule, name) || name} key={name}>

          {isCustom ? <DiffCustomizeData
              diffType={DiffType.PREV}
              config={configItem || {}}
              diffConfig={nextConfigItem}
            />
            : <DiffRenderSubConfigItem
              diffType={DiffType.PREV}
              diffConfig={nextValue}
              config={value}
              options={getConfigInfo(name, {}, configContent)?.options ?? []}
              fullConfig={getConfigInfo(name, {}, configContent)?.config ?? {}}
              dataSource={getConfigInfo(name, nextV7, configContent)?.config ?? {}}/>}
        </CollapsePanel>;
      })}
    </Collapse>;

  const nextConfigContent = (configContent: any[]) => <Collapse
    className={style.nextConfigContent}
    activeKey={activeKey}
    onChange={nextCollapseChange}
  >
    {prevSampleRatio === SampleRatio.ON ? <CollapsePanel
      className={classnames({
        [styles.globalSwitch]: true,
        [style.diffCollapsePanel]: !isEqual(prevSampleRatio, nextSampleRatio) })}
      header={renderSampleRatio(!!nextSampleRatio)}
      key={'sampleRatio'}
      showArrow={false}
      collapsible={'disabled'}
    /> : null}
    {configContent.map((configItem) => {
      const name = Object.keys(configItem)?.[0];
      const value = configItem[name];
      const prevConfigItem = prevChunkConfig.find(item => item[name]) || {};
      const prevValue = prevConfigItem?.[name] || {};
      const isCustom = isCustomize(
        { name }, androidFullConfig, iosFullConfig, flutterFullConfig,
        windowsFullConfig, harmonyOSFullConfig, fullConfigMode,
      )
        || isConfigManage(props.tab);

      return <CollapsePanel
        className={classnames({
          [style.diffCollapsePanel]: !isEqual(value, prevValue) })}
        header={(name || '').indexOf(CUSTOMIZE_CONFIG.name) === 0 ? get(platformModule, CUSTOMIZE_CONFIG.name) : get(platformModule, name) || name} key={name}>
        {isCustom ? <DiffCustomizeData
            config={configItem || {}}
            diffConfig={prevConfigItem}
            diffType={DiffType.NEXT}
          />
          : <DiffRenderSubConfigItem
            config={value}
            diffType={DiffType.NEXT}
            diffConfig={prevValue}
            options={getConfigInfo(name, {}, configContent)?.options ?? []}
            fullConfig={getConfigInfo(name, {}, configContent)?.config ?? {}}
            dataSource={getConfigInfo(name, nextV7, configContent)?.config ?? {}}/>}
      </CollapsePanel>;
    })}
  </Collapse>;
  const prevInfo = [
    { label: '任务名称', info: prevForm.name, isSame: isEqual(prevForm.name, nextForm.name) },
    { label: '配置项', info: prevConfig(diffForm.prevConfig), isSame: true },
    { label: '优先级', info: prevForm.priority, isSame: isEqual(prevForm.priority, nextForm.priority) },
    { label: '备注', info: prevForm.memo, isSame: isEqual(prevForm.memo, nextForm.memo) },
    { label: '配置内容', info: prevConfigContent(prevChunkConfig), isSame: true },
  ];

  const nextInfo = [
    { label: '任务名称', info: nextForm.name, isSame: isEqual(prevForm.name, nextForm.name) },
    { label: '配置项', info: nextConfig(diffForm.nextConfig), isSame: true },
    { label: '优先级', info: nextForm.priority, isSame: isEqual(prevForm.priority, nextForm.priority) },
    { label: '备注', info: nextForm.memo, isSame: isEqual(prevForm.memo, nextForm.memo) },
    { label: '配置内容', info: nextConfigContent(nextChunkConfig), isSame: true },
  ];

  return <Modal
    className={style.diffModal}
    visible={visible}
    closeIcon={<IconClose/>}
    title={'差异对比'}
    width={'1600px'}
    onCancel={close}
    destroyOnClose={true}
    footer={
      type === 'view' ? null : <Space size={16}>
      <Button className={style.submitButton} onClick={onSubmit} type="primary" disabled={!isDiffCode() && !isDiffForm()}>提交</Button>
      <Button className={style.closeButton} onClick={close}>取消</Button>
      </Space>
    }
  >
    <div className={style.diff}>
      <div className={style.prev}>
        <h1 className={style.prevTitle}>修改前</h1>
        <Descriptions column={1} labelStyle={labelStyle}>
          {prevInfo.map(item => <Descriptions.Item label={item.label} key={item.label}>
            {item.isSame ? item.info : <span style={{ color: '#F04352' }}>{item.info}</span>}
          </Descriptions.Item>)}
        </Descriptions>
      </div>
      <div className={style.diffIcon}>
        <DoubleRightOutlined />
      </div>
      <div className={style.next}>
        <h1 className={style.nextTitle}>修改后</h1>
        <Descriptions column={1} labelStyle={labelStyle}>
          {nextInfo.map(item => <Descriptions.Item label={item.label} key={item.label}>
            {item.isSame ? item.info : <span style={{ color: '#0A6CFF' }}>{item.info}</span>}
          </Descriptions.Item>)}
        </Descriptions>
      </div>
    </div>
  </Modal>;
});

export const getOpertor = (VersionOperator: string) => {
  if (VersionOperator) {
    return operatorOptions[VersionOperator as keyof typeof operatorOptions];
  }
  return '是';
};

const getDiffForm = (
  prevForm: IConfig,
  nextForm: IConfig,
  tab: ConfigTabType,
) => {
  const getConfig = (form: IConfig) => {
    const appVersionMode = getAppVersionModeLabel(form?.appVersionMode ?? []);
    const appVersionOperator = getOpertor(form?.appVersionOperator) || '';
    const sdkVersionOperator = getOpertor(form?.sdkVersionOperator) || '';
    const osVersionOperator = getOpertor(form?.osVersionOperator) || '';
    return [
      {
        label: '版本类型',
        hasValue: !isEmpty(appVersionMode),
        info: appVersionMode?.join?.('、') || '',
        isSame: isEqual(prevForm.appVersionMode, nextForm.appVersionMode),
      },
      {
        label: 'APP版本',
        hasValue: !isEmpty(form?.appVersion),
        info: `${appVersionOperator} ${form?.appVersion?.join?.('、') || ''}`,
        isSame: isEqual(prevForm.appVersion, nextForm.appVersion)
          && isEqual(prevForm.appVersionOperator, nextForm.appVersionOperator),
      },
      {
        label: 'SDK版本',
        hasValue: !isEmpty(form?.sdkVersion),
        info: `${sdkVersionOperator} ${form?.sdkVersion?.join?.('、') || ''}`,
        isSame: isEqual(prevForm.sdkVersion, nextForm.sdkVersion)
          && isEqual(prevForm.sdkVersionOperator, nextForm.sdkVersionOperator),
      },
      {
        label: '系统版本', hasValue: !isEmpty(form?.system),
        info: `${osVersionOperator} ${form?.system?.join?.('、') || ''}`,
        isSame: isEqual(prevForm.system, nextForm.system)
          && isEqual(prevForm.osVersionOperator, nextForm.osVersionOperator),
      },
      {
        label: '机型', hasValue: !isEmpty(form?.model),
        info: getOpertor('OR') + form?.model?.join?.('、') || '',
        isSame: isEqual(prevForm.model, nextForm.model) },
      {
        label: '用户白名单', hasValue: !isEmpty(form?.uin),
        info: getOpertor('OR') + form?.uin?.join?.('、') || '',
        isSame: isEqual(prevForm.uin, nextForm.uin) },
      {
        label: '设备白名单',
        hasValue: !isEmpty(form?.deviceId),
        info: getOpertor('OR') + form?.deviceId?.join?.('、') || '',
        isSame: isEqual(prevForm.deviceId, nextForm.deviceId) },
      {
        label: 'APP ID',
        hasValue: isConfigManage(tab) && !isEmpty(form?.productId),
        info: getOpertor('OR') + (form?.productId as string[])?.join?.('、') || '',
        isSame: isEqual(prevForm.productId, nextForm.productId) },
    ].filter(item => item.hasValue);
  };

  const prevConfig = getConfig(prevForm);
  const nextConfig = getConfig(nextForm);

  return {
    prevConfig,
    nextConfig,
  };
};
